import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Form, Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";

import { useUser } from "../../user/utils/user";

import { AgGridWithOverlay } from "../../common/components/AgGridWithOverlay";
import LoadingModal from "../../common/components/LoadingModal.js";
import DateInput from "../../common/components/form/DateInput.js";
import Select from "../../common/components/form/Select.js";
import { useRoleClientNavigate } from "../../common/hooks/useRoleClientNavigate";
import { get } from "../../common/utils/api";
import { calculatePercentage, formatDate } from "../../common/utils/helpers";
import { questionCodeMapper } from "./ReviewDetail";

function ReviewForm({ user, userOptions, t }) {
  return (
    <React.Fragment>
      <div className="space-y-6 pt-8 sm:space-y-5 sm:pt-10">
        <div className="space-y-6 sm:space-y-5">
          {user.user.is_superuser && (
            <Select
              label={"User email"}
              name={"formUser"}
              options={userOptions}
            />
          )}
          <DateInput label={"Start date"} name={"formStartDate"} />
          <DateInput label={"End date"} name={"formEndDate"} />
        </div>
      </div>
      <div className="py-5">
        <div className="flex justify-end">
          <button
            type="submit"
            className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-cyan-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:bg-gray-500 disabled:cursor-not-allowed"
          >
            {t("common.generate", "Generate Report")}
          </button>
        </div>
      </div>
    </React.Fragment>
  );
}

export function processGroupedByDate(data) {
  return data.groupedByDate.map((item) => ({
    ...item,
    reviewed_percent: calculatePercentage(item.review_complete, item.answers),
    incorrect_percent: calculatePercentage(
      item.incorrect_count,
      item.review_complete,
    ),
  }));
}
export function processGroupedByQuestions(data) {
  return data.groupedByQuestions.map((item) => {
    const code = item.question_code;
    return {
      ...item,
      question_code_decoded: questionCodeMapper[code] || "",
      incorrect_percent: calculatePercentage(
        item.incorrect_count,
        item.review_complete,
      ),
    };
  });
}

export function processGroupedByUsername(data) {
  const groupedByUsername = data.groupedByUsername.reduce((acc, item) => {
    acc[item.username] = { ...item, total_assigned: item.answers || 0 };
    return acc;
  }, {});
  // Merge queue counts
  data.queue.forEach(({ username, count: questions_remaining }) => {
    if (groupedByUsername[username]) {
      groupedByUsername[username].total_assigned += questions_remaining;
    } else {
      groupedByUsername[username] = {
        username,
        answers: 0,
        review_complete: 0,
        incorrect_count: 0,
        total_assigned: questions_remaining,
      };
    }
  });

  // Calculate percentages
  return Object.values(groupedByUsername).map((item) => ({
    ...item,
    reviewed_percent: calculatePercentage(item.review_complete, item.answers),
    incorrect_percent: calculatePercentage(
      item.incorrect_count,
      item.review_complete,
    ),
  }));
}

function ReviewerSummary({ showAlert, t }) {
  let [userOptions, setUserOptions] = useState([]);
  let [summaryReviewerData, setSummaryReviewerData] = useState(() => {
    try {
      const cached = sessionStorage.getItem("summaryReviewerData");
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      console.error("Failed to parse cached summaryReviewerData", e);
      return {};
    }
  });
  const [error, setError] = useState(null);

  const navigate = useRoleClientNavigate();
  const { user } = useUser();

  // Default date range: past three days for now
  let today = new Date();
  let daysBefore = new Date();
  daysBefore.setDate(today.getDate() - 3);

  // Use session storage for the existing user and dates if they exist
  const initialValues = {
    formUser: sessionStorage.getItem("user") || "All",
    formStartDate:
      sessionStorage.getItem("startDate") || formatDate(daysBefore),
    formEndDate: sessionStorage.getItem("endDate") || formatDate(today),
    formShowCompleted: sessionStorage.getItem("showCompleted") || "no",
  };

  const columnDefsByUsername = [
    { field: "username", headerName: t("user.user", "User"), width: 200 },
    {
      field: "total_assigned",
      headerName: "# Assigned",
      width: 150,
      type: "numericColumn",
    },
    {
      field: "datas",
      headerName: t("user.total", "# Data"),
      width: 150,
      type: "numericColumn",
    },
    {
      field: "answers",
      headerName: t("user.total", "# Answered"),
      width: 150,
      type: "numericColumn",
    },
    {
      field: "review_complete",
      headerName: t("user.review_complete", "# Reviewed"),
      width: 150,
      type: "numericColumn",
    },
    {
      field: "incorrect_count",
      headerName: t("Todo", "# Incorrect"),
      width: 150,
      type: "numericColumn",
    },
    {
      field: "reviewed_percent",
      headerName: t("user.review_complete", "% Reviewed"),
      width: 200,
      type: "numericColumn",
      valueFormatter: (params) => params.data.reviewed_percent.toFixed(2),
    },
    {
      field: "incorrect_percent",
      headerName: t("Todo", "% Incorrect"),
      width: 150,
      type: "numericColumn",
      valueFormatter: (params) => params.data.incorrect_percent.toFixed(2),
    },
  ];

  const columnDefsByDate = [
    { field: "username", headerName: t("user.user", "User"), width: 200 },
    { field: "date", headerName: t("common.date", "Date"), width: 150 },
    {
      field: "datas",
      headerName: t("user.total", "# Data"),
      width: 150,
      type: "numericColumn",
    },
    {
      field: "answers",
      headerName: t("user.total", "# Answered"),
      width: 150,
      type: "numericColumn",
    },
    {
      field: "review_complete",
      headerName: t("user.review_complete", "# Reviewed"),
      width: 200,
      type: "numericColumn",
    },
    {
      field: "incorrect_count",
      headerName: t("Todo", "# Incorrect"),
      width: 150,
      type: "numericColumn",
    },
    {
      field: "reviewed_percent",
      headerName: t("user.review_complete", "% Reviewed"),
      width: 200,
      type: "numericColumn",
      valueFormatter: (params) => params.data.reviewed_percent?.toFixed(2),
    },
    {
      field: "incorrect_percent",
      headerName: t("Todo", "% Incorrect "),
      width: 150,
      type: "numericColumn",
      valueFormatter: (params) => params.data.incorrect_percent?.toFixed(2),
    },
  ];

  const generateColumnDefsByQuestions = (startDate, endDate) => [
    { field: "username", headerName: t("user.user", "User") },
    {
      field: "question_code",
      headerName: t("question.question", "Question"),
      type: "numericColumn",
      tooltipField: "question_code_decoded",
      cellClass: ["text-right", "cursor-pointer"],
    },
    {
      field: "review_complete",
      headerName: t("user.review_complete", "# Reviewed"),
      type: "numericColumn",
    },
    {
      field: "incorrect_count",
      headerName: t("Todo", "# Incorrect"),
      type: "numericColumn",
      cellRenderer: (params) => params.value,
      onCellClicked: (params) => {
        navigate(
          `/reviewer/answer?user=${params.data.username}&question_code=${params.data.question_code}&start_date=${startDate}&end_date=${endDate}`,
        );
      },
      cellClass: [
        "text-red-600",
        "hover:text-red-900",
        "cursor-pointer",
        "text-right",
      ],
    },
    {
      field: "incorrect_percent",
      headerName: t("Todo", "% Incorrect"),
      type: "numericColumn",
      valueFormatter: (params) => params.data.incorrect_percent?.toFixed(2),
    },
  ];

  const getUsers = async () => {
    get("/users/").then((response) => {
      let uniqueUsers = Object.assign(
        { All: "All" },
        ...response.data
          .filter((x) => x.is_reviewer)
          .sort(function (a, b) {
            return a.first_name.localeCompare(b.first_name);
          })
          .map((x) => ({
            [x.email]: x.first_name + " " + x.last_name + " (" + x.email + ")",
          })),
      );
      setUserOptions(uniqueUsers);
    });
  };

  const getData = async (
    user_selected,
    start_date,
    end_date,
    show_completed,
    setSubmitting,
  ) => {
    get(
      `/data/review/summary/?username=${user_selected}&start_date=${start_date}&end_date=${end_date}`,
    )
      .then((response) => response.data)
      .then((data) => {
        const groupedByUsername = processGroupedByUsername(data);
        const groupedByQuestions = processGroupedByQuestions(data);
        const groupedByDate = processGroupedByDate(data);
        sessionStorage.setItem(
          "summaryReviewerData",
          JSON.stringify({
            groupedByUsername,
            groupedByQuestions,
            groupedByDate,
            queueData: data.queue,
            startDate: start_date,
            endDate: end_date,
          }),
        );
        setSummaryReviewerData({
          groupedByUsername: groupedByUsername,
          groupedByQuestions: groupedByQuestions,
          groupedByDate: groupedByDate,
          queueData: data.queue,
          startDate: start_date,
          endDate: end_date,
        });
      })
      .catch((error) => {
        console.error(error);
        setError(error);
      })
      .finally(() => setTimeout(() => setSubmitting(false), 300));
  };

  const gridUsername = useRef(null);
  const gridDate = useRef(null);
  const gridQuestions = useRef(null);

  const onGridReady = (params, grid) => {
    grid.current = params.api;
    params.api.sizeColumnsToFit();
  };

  useEffect(() => {
    getUsers();
  }, []);
  const columnDefsByQuestions = generateColumnDefsByQuestions(
    summaryReviewerData.startDate || initialValues.formStartDate,
    summaryReviewerData.endDate || initialValues.formEndDate,
  );

  const exportData = (grid) => {
    grid.current.exportDataAsCsv({
      allColumns: true,
      processCellCallback: (v) =>
        v.column.colDef.type === "combinedNumericColumn" ? v.value[0] : v.value,
    });
  };

  function onSubmit(value, { setSubmitting }) {
    getData(
      value.formUser,
      value.formStartDate,
      value.formEndDate,
      value.formShowCompleted,
      setSubmitting,
    );
    sessionStorage.setItem("user", value.formUser);
    sessionStorage.setItem("startDate", value.formStartDate);
    sessionStorage.setItem("endDate", value.formEndDate);
    sessionStorage.setItem("showCompleted", value.formShowCompleted);
  }

  return (
    <React.Fragment>
      <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 bg-white border-gray-200 shadow rounded-md pt-6">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {t("common.summary", "Generate Review Summary")}
            </h3>
          </div>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={onSubmit}
          >
            {({ isSubmitting }) => (
              <React.Fragment>
                <LoadingModal open={isSubmitting} />
                <Form className="space-y-8 divide-y divide-gray-200">
                  <ReviewForm user={user} userOptions={userOptions} t={t} />
                </Form>
              </React.Fragment>
            )}
          </Formik>
        </div>
      </div>
      <div className="mt-8 flex flex-col">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {t("common.reviewSummary", "Overall Review Activity")}
            </h3>
          </div>
        </div>
        <div className="mb-2 flex justify-end">
          <button
            onClick={() => exportData(gridUsername)}
            className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-cyan-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:bg-gray-500 disabled:cursor-not-allowed"
          >
            {t("common.exportToCSV", "Export to CSV")}
          </button>
        </div>
        <div data-testid="summaryByUser">
          <div className="ag-theme-alpine mt-4" style={{ height: 400 }}>
            <AgGridWithOverlay
              ref={gridUsername}
              rowData={summaryReviewerData.groupedByUsername}
              columnDefs={columnDefsByUsername}
              defaultColDef={{
                sortable: true,
                resizable: true,
              }}
              onGridReady={(params) => onGridReady(params, gridUsername)}
              error={error}
              t={t}
            />
          </div>
        </div>
      </div>
      <div className="mt-8 flex flex-col">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {t("common.reviewSummary", "Daily Review Activity")}
            </h3>
          </div>
        </div>
        <div className="mb-2 flex justify-end">
          <button
            onClick={() => exportData(gridDate)}
            className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-cyan-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:bg-gray-500 disabled:cursor-not-allowed"
          >
            {t("common.exportToCSV", "Export to CSV")}
          </button>
        </div>
        <div data-testid="summaryByDate">
          <div className="ag-theme-alpine mt-4" style={{ height: 400 }}>
            <AgGridWithOverlay
              ref={gridDate}
              rowData={summaryReviewerData.groupedByDate}
              columnDefs={columnDefsByDate}
              defaultColDef={{
                sortable: true,
                resizable: true,
              }}
              onGridReady={(params) => onGridReady(params, gridDate)}
              error={error}
              t={t}
            />
          </div>
        </div>
      </div>
      <div className="mt-8 flex flex-col">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {t("common.reviewSummary", "Most Common Mistakes")}
            </h3>
          </div>
        </div>
        <div className="mb-2 flex justify-end">
          <button
            onClick={() => exportData(gridQuestions)}
            className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-cyan-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:bg-gray-500 disabled:cursor-not-allowed"
          >
            {t("common.exportToCSV", "Export to CSV")}
          </button>
        </div>
        <div data-testid="summaryByQuestions">
          <div className="ag-theme-alpine mt-4" style={{ height: 400 }}>
            <AgGridWithOverlay
              ref={gridQuestions}
              rowData={summaryReviewerData.groupedByQuestions}
              columnDefs={columnDefsByQuestions}
              defaultColDef={{
                sortable: true,
                resizable: true,
              }}
              onGridReady={(params) => onGridReady(params, gridQuestions)}
              error={error}
              t={t}
            />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}
export default withTranslation()(ReviewerSummary);
